import { Typography } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useEffect, useState } from 'react';
import { TextComponent } from '../../../components/UI/PreviewModal/PreviewModal';
import { useAuthStore } from '../../../store/storeAuth';
import { useNotificationStore } from '../../../store/storeNotifications';
import { INode } from '../../NewFormBuilder/Context/FormBuilderContext';
import { usePublishedForm } from '../Context/PublishedFormContext';

interface OutputProps {
  node: INode;
}

export function Output({ node }: OutputProps) {
  const { state } = usePublishedForm();
  const [editorContent, setEditorContent] = useState('');
  const [standardContent, setStandardContent] = useState('');

  const user = useAuthStore(state => state.user);

  const setNotification = useNotificationStore(state => state.setNotification);

  useEffect(() => {
    if (node?.data?.fieldType === 'editor') {
      const outputById = state.variablesOutputs.find(
        item => item.outputId === node.data.varDataOutputId,
      );

      setEditorContent(outputById?.text || '');
    }

    if (node.data.fieldType === 'standard') {
      const outputById = state.variablesOutputs.find(
        item => item.outputId === node.data.varDataOutputId,
      );

      setStandardContent(outputById?.text || '');
    }
  }, [node, state.variablesOutputs]);

  const handleEditorChange = (content: string) => {
    setEditorContent(content);
  };

  const onCopy = (text: string) => {
    navigator.clipboard.writeText(text);

    setNotification({
      type: 'success',
      message: 'Text copied to clipboard',
    });
  };

  const isStandard = node.data.fieldType === 'standard';
  const isEditor = node.data.fieldType === 'editor';

  const styles = node.data.styles;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <div style={{ flexShrink: 0, marginBottom: '8px' }}>
        <Typography
          style={{
            fontFamily: styles?.title?.fontFamily || 'Roboto, sans-serif',
            fontWeight: styles?.title?.isBold ? '700' : '400',
            fontStyle: styles?.title?.isItalic ? 'italic' : 'normal',
            textDecoration: styles?.title?.isUnderlined ? 'underline' : 'none',
            textAlign: styles?.title?.textAlign || 'center',
            fontSize: `${styles?.title?.fontSize || 16}px`,
            color: styles?.title?.color || '#1F2937',
            width: '100%',
            lineHeight: '150%',
            letterSpacing: '0.5px',
          }}
        >
          {node.data.title}
        </Typography>

        <Typography
          variant="body1"
          style={{
            color: styles.subtitle?.color || 'rgb(71, 84, 103)',
            whiteSpace: 'pre-wrap',
            fontSize: `${styles.subtitle?.fontSize || 14}px`,
            lineHeight: '150%',
            fontFamily: styles.subtitle?.fontFamily || 'Roboto, sans-serif',
            fontWeight: styles.subtitle?.isBold ? '700' : '400',
            fontStyle: styles.subtitle?.isItalic ? 'italic' : 'normal',
            textDecoration: styles.subtitle?.isUnderlined
              ? 'underline'
              : 'none',
            width: '100%',
            textAlign: styles.subtitle?.textAlign || 'left',
            letterSpacing: '0.5px',
          }}
        >
          {node.data.subtitle}
        </Typography>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isStandard && (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              height: '90%',
              marginBottom: '17px',
            }}
          >
            <TextComponent
              item={{
                text: standardContent,
                label: 'Output',
                edit: false,
                name: 'output',
              }}
              onCopy={onCopy}
              user={user}
              processedSegments={standardContent}
              size={{ height: '90%' }}
              showDownload={true}
            />
          </div>
        )}

        {isEditor && (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              height: '100%',
            }}
          >
            <Editor
              key={user?.theme}
              id={node.id}
              apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
              cloudChannel="5"
              scriptLoading={{
                async: true,
              }}
              value={editorContent}
              onEditorChange={handleEditorChange}
              init={{
                branding: false,
                skin: user?.theme === 'dark' ? 'oxide-dark' : '',
                content_css: user?.theme === 'dark' ? 'dark' : '',
                resize: false,
                width: '100%',
                height: '100%',
                plugins: [
                  'advlist autolink lists link image',
                  'wordcount',
                  'paste',
                  'code',
                  'fullpage',
                  'table',
                  'charmap print preview anchor help',
                  'searchreplace visualblocks code',
                  'insertdatetime media table paste wordcount',
                ],
                toolbar:
                  'undo redo | formatselect | bold italic | \
              alignleft aligncenter alignright | \
              bullist numlist outdent indent | wordcount | code | fullpage | tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
                code_dialog_width: 800,
                code_dialog_height: 600,
                valid_elements: '*[*]',
                extended_valid_elements: '*[*]',
                allow_script_urls: true,
                allow_unsafe_link_target: true,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
